import { get, set } from '../store';

export interface Weather {
  temp: number;
  code: number;
  isDay: boolean;
  daily: Array<{ date: string; code: number; min: number; max: number }>;
  fetchedAt: number;
}

const TTL = 15 * 60_000;
const memory = new Map<string, Weather>();

/** Open-Meteo (no API key). Cached in memory + IndexedDB so it keeps working offline. */
export async function getWeather(
  lat: number,
  lng: number,
  timeZone: string,
): Promise<Weather | null> {
  const key = `weather:${lat.toFixed(2)},${lng.toFixed(2)}`;
  const mem = memory.get(key);
  if (mem && Date.now() - mem.fetchedAt < TTL) return mem;
  try {
    const params = new URLSearchParams({
      latitude: String(lat),
      longitude: String(lng),
      current: 'temperature_2m,weather_code,is_day',
      daily: 'weather_code,temperature_2m_max,temperature_2m_min',
      timezone: timeZone || 'auto',
      forecast_days: '6',
    });
    const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`, {
      signal: AbortSignal.timeout(10_000),
    });
    if (!res.ok) throw new Error(String(res.status));
    const d = (await res.json()) as {
      current: { temperature_2m: number; weather_code: number; is_day: number };
      daily: {
        time: string[];
        weather_code: number[];
        temperature_2m_max: number[];
        temperature_2m_min: number[];
      };
    };
    const w: Weather = {
      temp: d.current.temperature_2m,
      code: d.current.weather_code,
      isDay: d.current.is_day === 1,
      daily: d.daily.time.map((date, i) => ({
        date,
        code: d.daily.weather_code[i]!,
        max: d.daily.temperature_2m_max[i]!,
        min: d.daily.temperature_2m_min[i]!,
      })),
      fetchedAt: Date.now(),
    };
    memory.set(key, w);
    await set(key, w);
    return w;
  } catch {
    const cached = mem ?? (await get<Weather>(key));
    // Offline: show cached data for up to 24 h.
    return cached && Date.now() - cached.fetchedAt < 86_400_000 ? cached : null;
  }
}

/** Current temperature near the screen for {{weer.temp}} in presentations. */
let lastTemp: number | null = null;
export function lastKnownTemp(): number | null {
  return lastTemp;
}
export async function refreshTemp(lat: number, lng: number, tz: string): Promise<void> {
  const w = await getWeather(lat, lng, tz);
  if (w) lastTemp = w.temp;
}

/** Simple inline SVG icons for WMO weather codes. */
export function weatherIcon(code: number, isDay: boolean): string {
  const sun = `<circle cx="32" cy="32" r="11" fill="#FBBF24"/>${Array.from({ length: 8 }, (_, i) => `<line x1="32" y1="8" x2="32" y2="15" stroke="#FBBF24" stroke-width="4" stroke-linecap="round" transform="rotate(${i * 45} 32 32)"/>`).join('')}`;
  const moon = `<path d="M40 14a18 18 0 1 0 10 30A16 16 0 0 1 40 14z" fill="#E2E8F0"/>`;
  const cloud = (y = 0, fill = '#E2E8F0') =>
    `<path d="M20 ${46 + y}h26a10 10 0 0 0 0-20 14 14 0 0 0-26-3 9 9 0 0 0 0 23z" fill="${fill}"/>`;
  const rain = `<g stroke="#38BDF8" stroke-width="3.5" stroke-linecap="round"><line x1="24" y1="52" x2="21" y2="60"/><line x1="34" y1="52" x2="31" y2="60"/><line x1="44" y1="52" x2="41" y2="60"/></g>`;
  const snow = `<g fill="#F8FAFC"><circle cx="24" cy="56" r="3"/><circle cx="34" cy="59" r="3"/><circle cx="44" cy="56" r="3"/></g>`;
  const bolt = `<path d="M34 46l-8 12h7l-3 10 10-14h-7l3-8z" fill="#FBBF24"/>`;
  let inner: string;
  if (code === 0) inner = isDay ? sun : moon;
  else if (code <= 2)
    inner = `<g transform="translate(-6 -6) scale(.8)">${isDay ? sun : moon}</g>${cloud(4)}`;
  else if (code === 3 || code === 45 || code === 48) inner = cloud(0, '#CBD5E1');
  else if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82))
    inner = cloud(-6, '#CBD5E1') + rain;
  else if ((code >= 71 && code <= 77) || code === 85 || code === 86) inner = cloud(-6) + snow;
  else if (code >= 95) inner = cloud(-6, '#94A3B8') + bolt;
  else inner = cloud();
  return `<svg viewBox="0 0 64 64" aria-hidden="true">${inner}</svg>`;
}
